import { useCallback, useMemo, useReducer, useRef } from "react";
import { ProductsContext } from "./ProductsContext";
import { getApiService } from "../services/apiService";

const CACHE_TTL_MS = 5 * 60 * 1000;

function normalizeFilters(filters = {}) {
  const normalized = {};

  if (filters.brand_id !== undefined && filters.brand_id !== null) {
    const brandId = String(filters.brand_id).trim();
    if (brandId) {
      normalized.brand_id = brandId;
    }
  }

  if (filters.god_proizv !== undefined && filters.god_proizv !== null) {
    const year = String(filters.god_proizv).trim();
    if (year) {
      normalized.god_proizv = year;
    }
  }

  return normalized;
}

function serializeFilters(filters = {}) {
  const normalized = normalizeFilters(filters);
  const keys = Object.keys(normalized).sort();

  if (keys.length === 0) {
    return "all";
  }

  return keys.map((key) => `${key}:${normalized[key]}`).join("|");
}

const initialState = {
  cache: {},
  loading: false,
  loadingCount: 0,
  error: null,
  filters: {},
  sortBy: null,
};

function reducer(state, action) {
  switch (action.type) {
    case "FETCH_START": {
      const loadingCount = state.loadingCount + 1;
      return {
        ...state,
        loadingCount,
        loading: loadingCount > 0,
        error: null,
      };
    }
    case "FETCH_SUCCESS": {
      const loadingCount = Math.max(0, state.loadingCount - 1);
      return {
        ...state,
        cache: {
          ...state.cache,
          [action.cacheKey]: {
            data: action.payload,
            expires: action.expires,
          },
        },
        loadingCount,
        loading: loadingCount > 0,
      };
    }
    case "FETCH_ERROR": {
      const loadingCount = Math.max(0, state.loadingCount - 1);
      return {
        ...state,
        loadingCount,
        loading: loadingCount > 0,
        error: action.payload,
      };
    }
    case "SET_FILTERS":
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
      };
    case "RESET_FILTERS":
      return {
        ...state,
        filters: {},
      };
    case "SET_SORT":
      return {
        ...state,
        sortBy: action.payload,
      };
    default:
      return state;
  }
}

export function ProductsProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const api = useMemo(() => getApiService(), []);
  const cacheRef = useRef({});
  const pendingRef = useRef(new Map());

  // Cache updates must not reload every card list or duplicate pending requests.
  const fetchCached = useCallback((cacheKey, load) => {
    const cached = cacheRef.current[cacheKey];
    if (cached && cached.expires > Date.now()) {
      return Promise.resolve(cached.data);
    }
    if (pendingRef.current.has(cacheKey)) {
      return pendingRef.current.get(cacheKey);
    }
    dispatch({ type: "FETCH_START" });
    const request = Promise.resolve().then(load).then((data) => {
      const expires = Date.now() + CACHE_TTL_MS;
      cacheRef.current[cacheKey] = { data, expires };
      dispatch({ type: "FETCH_SUCCESS", cacheKey, payload: data, expires });
      return data;
    }).catch((error) => {
      dispatch({ type: "FETCH_ERROR", payload: error?.message || "Ошибка загрузки продуктов" });
      throw error;
    }).finally(() => pendingRef.current.delete(cacheKey));
    pendingRef.current.set(cacheKey, request);
    return request;
  }, []);

  const getCachedProducts = useCallback(
    (cacheKey) => {
      const cached = state.cache[cacheKey];
      if (cached && cached.expires > Date.now()) {
        return cached.data;
      }
      return null;
    },
    [state.cache],
  );

  const getProducts = useCallback(
    async (categoryId, filters = {}) => {
      const normalizedFilters = normalizeFilters(filters);
      const cacheKey = `group:${categoryId}:${serializeFilters(normalizedFilters)}`;
      return fetchCached(cacheKey, () => api.getProductsBack(categoryId, normalizedFilters));
    },
    [api, fetchCached],
  );

  const getProductsForGroups = useCallback(
    async (groupIds = [], options = {}) => {
      const normalizedIds = Array.isArray(groupIds)
        ? groupIds.filter(Boolean)
        : [];
      const sortedIds = [...normalizedIds].sort((a, b) => a - b);
      const normalizedFilters = normalizeFilters(options.filters || {});
      const filterKey = serializeFilters(normalizedFilters);
      const cacheKey =
        options.cacheKey ||
        `groups:${sortedIds.join(",") || "empty"}:${filterKey}`;

      return fetchCached(cacheKey, async () => {
        const results = await Promise.allSettled(
          sortedIds.map((groupId) => getProducts(groupId, normalizedFilters)),
        );

        const fulfilledResults = results.filter(
          (result) => result.status === "fulfilled",
        );
        const rejectedResults = results.filter(
          (result) => result.status === "rejected",
        );

        if (sortedIds.length > 0 && fulfilledResults.length === 0) {
          const firstError = rejectedResults[0]?.reason;
          throw firstError || new Error("Ошибка загрузки списка продуктов");
        }

        const products = fulfilledResults.flatMap((result) => result.value);

        return products;
      });
    },
    [getProducts, fetchCached],
  );

  const setFilters = useCallback((filters) => {
    dispatch({ type: "SET_FILTERS", payload: filters });
  }, []);

  const resetFilters = useCallback(() => {
    dispatch({ type: "RESET_FILTERS" });
  }, []);

  const setSortBy = useCallback((sortBy) => {
    dispatch({ type: "SET_SORT", payload: sortBy });
  }, []);

  const value = useMemo(
    () => ({
      state,
      getCachedProducts,
      getProducts,
      getProductsForGroups,
      setFilters,
      resetFilters,
      setSortBy,
    }),
    [
      state,
      getCachedProducts,
      getProducts,
      getProductsForGroups,
      setFilters,
      resetFilters,
      setSortBy,
    ],
  );

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
}
